import { useNavigate } from "react-router-dom";
import { mutate } from "swr";
import { useSubmitForm } from "../../../hooks/useSubmitForm";
import { changeRequestsService } from "../../api_client";
import type {
  ChangeRequestDto,
  CreateChangeRequestDto,
  UpdateChangeRequestDto,
} from "../../../api";
import { AppRoutes } from "../../../common/routes/app_routes";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";
import { useMePartner } from "../../../swr";
import { getChangeRequestsListPath } from "../data/change-request-list-path";
import { MY_PROFILE_CHANGE_REQUEST_KEY } from "./useMyPendingProfileChangeRequest";

function extractErrorMessage(error: unknown, fallback: string): string {
  const typed = error as { response?: { data?: { message?: string | string[] } }; message?: string };
  const raw = typed?.response?.data?.message ?? typed?.message;
  if (Array.isArray(raw)) return raw.join(", ");
  return raw ?? fallback;
}

async function invalidateChangeRequestCaches(id?: string) {
  await mutate(
    (key) =>
      (Array.isArray(key) && key[0] === MY_PROFILE_CHANGE_REQUEST_KEY) ||
      (typeof key === "string" &&
        (key.startsWith("my-change-requests") ||
          (id ? key === `change-request-details-${id}` : false))),
  );
}

export function useCreateChangeRequest(
  buildDto: () => CreateChangeRequestDto,
  options: { submitAfter?: boolean } = {},
) {
  const navigate = useNavigate();
  const { isBuyer, isSeller } = useMePartner();
  const listPath = getChangeRequestsListPath({ isBuyer, isSeller });
  const { submitAfter = false } = options;

  return useSubmitForm<CreateChangeRequestDto, ChangeRequestDto>({
    submitMethod: async () => {
      const dto = buildDto();
      const created = await changeRequestsService.create(dto);
      if (submitAfter && created.data?.id) {
        return await changeRequestsService.submit(created.data.id);
      }
      return created;
    },
    runAfterSuccess: async (data) => {
      showSuccessToast(
        submitAfter ? "Change request submitted for review." : "Draft saved.",
      );
      await invalidateChangeRequestCaches(data?.id);
      if (data?.id) {
        navigate(AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", data.id));
      } else {
        navigate(listPath);
      }
    },
    runAfterError: (error) => {
      showErrorToast(extractErrorMessage(error, "Failed to create change request."));
    },
  });
}

export function useUpdateChangeRequest(
  id: string,
  buildDto: () => UpdateChangeRequestDto,
  options: { submitAfter?: boolean } = {},
) {
  const navigate = useNavigate();
  const { submitAfter = false } = options;

  return useSubmitForm<UpdateChangeRequestDto, ChangeRequestDto>({
    submitMethod: async () => {
      const dto = buildDto();
      const updated = await changeRequestsService.update(id, dto);
      if (submitAfter) {
        return await changeRequestsService.submit(id);
      }
      return updated;
    },
    runAfterSuccess: async () => {
      showSuccessToast(
        submitAfter ? "Change request submitted for review." : "Draft updated.",
      );
      await invalidateChangeRequestCaches(id);
      navigate(AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", id));
    },
    runAfterError: (error) => {
      showErrorToast(extractErrorMessage(error, "Failed to update change request."));
    },
  });
}

export function useSubmitChangeRequest(id: string) {
  return useSubmitForm<unknown, ChangeRequestDto>({
    submitMethod: async () => await changeRequestsService.submit(id),
    runAfterSuccess: async () => {
      showSuccessToast("Change request submitted for review.");
      await invalidateChangeRequestCaches(id);
    },
    runAfterError: (error) => {
      showErrorToast(extractErrorMessage(error, "Failed to submit change request."));
    },
  });
}

export function useCancelChangeRequest(id: string) {
  return useSubmitForm<unknown, ChangeRequestDto>({
    submitMethod: async () => await changeRequestsService.cancel(id),
    runAfterSuccess: async () => {
      showSuccessToast("Change request cancelled.");
      await invalidateChangeRequestCaches(id);
    },
    runAfterError: (error) => {
      showErrorToast(extractErrorMessage(error, "Failed to cancel change request."));
    },
  });
}
